import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarBlank, Clock, MapPin, VideoCamera, ArrowsClockwise, X } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../lib/api";

function formatDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function MyAppointments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/appointments");
        setData(data);
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const { upcoming, past } = useMemo(() => {
    const list = data?.appointments || [];
    const now = Date.now();
    const up = [];
    const pa = [];
    for (const a of list) {
      const t = a.start_time ? new Date(a.start_time).getTime() : 0;
      if (a.status === "active" && t >= now) up.push(a);
      else pa.push(a);
    }
    up.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
    pa.sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
    return { upcoming: up, past: pa };
  }, [data]);

  if (loading) {
    return (
      <div data-testid="my-appointments-loading" className="p-6 bg-white border border-stone-200/60 rounded-md text-sm text-ink-muted">
        Loading appointments…
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="my-appointments-error" className="p-6 bg-white border border-stone-200/60 rounded-md text-sm text-destructive">
        {error}
      </div>
    );
  }

  // Feature flag off — don't render anything noisy
  if (data && data.configured === false) return null;

  return (
    <div data-testid="my-appointments" className="p-6 bg-white border border-stone-200/60 rounded-md">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">My appointments</div>
          <h2 className="font-serif text-2xl mt-2">Your visits with the clinic.</h2>
        </div>
        <Link to="/enquiry" className="text-sm text-bronze-dark hover:text-bronze">+ Book another visit</Link>
      </div>

      {upcoming.length === 0 && past.length === 0 ? (
        <div data-testid="my-appointments-empty" className="mt-6 p-6 bg-cream-100 border border-bronze-light/40 rounded-md text-sm text-ink-muted">
          No appointments to show yet. If you've booked with us before using a different email, please <Link to="/contact" className="text-bronze-dark hover:underline">let us know</Link> and we'll link your bookings to this account.
          <div className="mt-3">
            <Link to="/enquiry" className="btn-primary inline-block">Book your first appointment</Link>
          </div>
        </div>
      ) : (
        <>
          {upcoming.length > 0 && (
            <div className="mt-6">
              <h3 className="text-xs uppercase tracking-[0.18em] text-ink-muted">Upcoming</h3>
              <ul className="mt-3 divide-y divide-stone-200/60" data-testid="my-appointments-upcoming">
                {upcoming.map((a) => <AppointmentRow key={a.id} a={a} />)}
              </ul>
            </div>
          )}
          {past.length > 0 && (
            <div className="mt-8">
              <h3 className="text-xs uppercase tracking-[0.18em] text-ink-muted">Past visits</h3>
              <ul className="mt-3 divide-y divide-stone-200/60" data-testid="my-appointments-past">
                {past.map((a) => <AppointmentRow key={a.id} a={a} past />)}
              </ul>
            </div>
          )}
        </>
      )}

      <p className="mt-6 text-xs text-ink-muted/80">
        Appointments are managed through our secure scheduling provider. Reschedule and cancel links open their hosted page in a new tab.
      </p>
    </div>
  );
}

function AppointmentRow({ a, past }) {
  const isCanceled = a.status && a.status !== "active";
  return (
    <li data-testid={`appointment-${a.id}`} className="py-4 flex flex-col sm:flex-row sm:items-start gap-4">
      <div className="sm:w-44 shrink-0 text-bronze-dark flex sm:flex-col gap-2 sm:gap-1 items-baseline sm:items-start">
        <CalendarBlank size={18} weight="duotone" />
        <div className="font-serif text-base text-ink">{formatDateTime(a.start_time)}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-serif text-lg">{a.event_name}</span>
          {isCanceled && (
            <span className="text-[10px] uppercase tracking-widest rounded-full border border-destructive/40 text-destructive px-2 py-0.5">
              {a.status}
            </span>
          )}
        </div>
        <div className="mt-1 text-sm text-ink-muted flex flex-wrap gap-x-4 gap-y-1">
          {a.consultant && <span className="inline-flex items-center gap-1"><Clock size={13}/> with {a.consultant}</span>}
          {a.location && <span className="inline-flex items-center gap-1"><MapPin size={13}/> {a.location}</span>}
          {a.join_url && (
            <a href={a.join_url} target="_blank" rel="noopener noreferrer"
               data-testid={`appointment-join-${a.id}`}
               className="inline-flex items-center gap-1 text-bronze-dark hover:underline">
              <VideoCamera size={13}/> Join video
            </a>
          )}
        </div>
      </div>
      {!past && !isCanceled && (
        <div className="flex items-center gap-3 shrink-0">
          {a.reschedule_url && (
            <a href={a.reschedule_url} target="_blank" rel="noopener noreferrer"
               data-testid={`appointment-reschedule-${a.id}`}
               className="text-sm text-bronze-dark hover:text-bronze inline-flex items-center gap-1">
              <ArrowsClockwise size={14}/> Reschedule
            </a>
          )}
          {a.cancel_url && (
            <a href={a.cancel_url} target="_blank" rel="noopener noreferrer"
               data-testid={`appointment-cancel-${a.id}`}
               className="text-sm text-ink hover:text-destructive inline-flex items-center gap-1">
              <X size={14}/> Cancel
            </a>
          )}
        </div>
      )}
    </li>
  );
}
